import { useCallback, useState } from 'react';
import { LatLng, Address } from '@hero/schema';
import { useAddressApi } from '@hero/api-hooks';
import { MapLocationData, LocationChangeHandler } from '@hero/web-components';

export interface UseMapInteractionsOptions {
  /** Callback when location changes */
  onLocationChange?: LocationChangeHandler;
  /** Whether map click selection is enabled */
  enableMapClick?: boolean;
  /** Whether marker dragging is enabled */
  enableMarkerDrag?: boolean;
}

/**
 * Hook for handling map interactions
 * Handles map clicks, marker drags, location selection, and reverse geocoding.
 * Uses useAddressApi() internally for address lookup.
 */
export function useMapInteractions(options: UseMapInteractionsOptions = {}) {
  const { onLocationChange, enableMapClick = true, enableMarkerDrag = true } = options;

  const addressApi = useAddressApi();
  const [isUpdatingAddress, setIsUpdatingAddress] = useState(false);

  /**
   * Reverse geocode coordinates to get address
   */
  const reverseGeocode = useCallback(
    async (lat: number, lng: number): Promise<Address | null> => {
      try {
        setIsUpdatingAddress(true);
        const data = await addressApi.mutateAsync({ lat, lng });
        return data || null;
      } catch (error) {
        console.error('Error reverse geocoding:', error);
        return null;
      } finally {
        setIsUpdatingAddress(false);
      }
    },
    [addressApi.mutateAsync],
  );

  /**
   * Handle map click to select location
   */
  const onMapClick = useCallback(
    async (event: google.maps.MapMouseEvent) => {
      if (!enableMapClick || !event.latLng) return;

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const cords: LatLng = { lat, lng };

      // Create location data with loading state
      const locationData: MapLocationData = {
        cords,
        isLoadingAddress: true,
        isAccurateLocation: false, // Map clicks are inaccurate by default
      };

      // Reverse geocode to get address
      const address = await reverseGeocode(lat, lng);

      if (address) {
        locationData.address = address;
        locationData.fullAddress = address.fullAddress;
        locationData.city = address.city?.name;
        locationData.isLoadingAddress = false;
      } else {
        locationData.isLoadingAddress = false;
      }

      // Call the callback if provided
      // Note: The parent component should handle adding this to locations array
      // This hook only provides the location data
      return locationData;
    },
    [enableMapClick, reverseGeocode],
  );

  /**
   * Handle marker drag end
   */
  const onMarkerDragEnd = useCallback(
    async (
      event: google.maps.MapMouseEvent,
      index: number,
      currentLocations: MapLocationData[],
    ) => {
      if (!enableMarkerDrag || !event.latLng) return;

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const cords: LatLng = { lat, lng };

      const currentLocation = currentLocations[index];
      if (!currentLocation) return;

      // Create updated location data
      // When dragging, mark as potentially inaccurate (unless it's a saved location)
      const updatedLocation: MapLocationData = {
        ...currentLocation,
        cords,
        isLoadingAddress: true,
        isAccurateLocation: false, // Any coordinate change during editing = inaccurate until confirmed
      };

      // Reverse geocode to get new address
      const address = await reverseGeocode(lat, lng);

      if (address) {
        updatedLocation.address = address;
        updatedLocation.fullAddress = address.fullAddress;
        updatedLocation.city = address.city?.name;
        updatedLocation.isLoadingAddress = false;
      } else {
        updatedLocation.isLoadingAddress = false;
      }

      // Call the callback if provided
      if (onLocationChange) {
        onLocationChange(updatedLocation, index);
      }

      return updatedLocation;
    },
    [enableMarkerDrag, reverseGeocode, onLocationChange],
  );

  return {
    onMapClick,
    onMarkerDragEnd,
    reverseGeocode,
    isUpdatingAddress,
  };
}
